/* eslint-disable react/no-array-index-key */
import React from 'react'
import { cn } from 'cn'
import isEqual from 'lodash/isEqual'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'

export type PossibleValue = string | number | object | boolean

export type Option<T extends PossibleValue> = {
  label: React.ReactElement | string
  value: T
  description?: string | React.JSX.Element
}

export type Props<T extends PossibleValue = string> = {
  label?: string
  description?: string
  name: string
  options: Option<T>[]
  value: T | null
  onChange: (value: T) => void
  error?: boolean
  columns?: number
  questionId?: number
  newDesign?: boolean
}

export default function RadioGroup<T extends PossibleValue = string>({
  label,
  description,
  error,
  options,
  value,
  name,
  onChange,
  columns,
  questionId,
  newDesign
}: Props<T>) {
  return (
    <div
      aria-describedby={`description-${questionId}`}
      aria-labelledby={`label-${questionId}`}
      className="w-full relative"
      role="radiogroup"
    >
      {error && (
        <div className="absolute inset-y-0 right-0 top-0 pr-3 pointer-events-none">
          <ExclamationCircleIcon
            aria-hidden="true"
            className="h-5 w-5 text-red-500"
          />
        </div>
      )}
      <p
        className={cn('block mb-2 pr-2', {
          'font-theme-heading font-bold text-gray-900': newDesign,
          ' text-base font-medium text-gray-700': error
        })}
        id={`label-${questionId}`}
      >
        {label}
      </p>
      <FieldDescription newDesign={newDesign}>{description}</FieldDescription>
      <FieldError>{error}</FieldError>
      <div style={{ columnCount: columns }}>
        {options.map((option, index) => (
          <div
            key={`${option.label.toString()}-${option.value.toString()}-${option.value.toString()}-${index}`}
            className="flex items-center"
          >
            <label className="inline-flex items-center mt-2">
              <input
                aria-checked={isEqual(option.value, value)}
                checked={isEqual(option.value, value)}
                className="text-orange-500 focus:border-orange-500 focus:ring-orange-500/50 rounded-full border-gray-300 shadow-xs focus:ring-3 scroll-offset"
                name={name}
                onChange={() => onChange(option.value)}
                type="radio"
              />
              <span className="ml-3 text-gray-900">{option.label}</span>
            </label>
            {option.description && (
              <span className="mt-2">{option.description}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
